import { h, Component, Host, Prop, Watch } from '@stencil/core';

import { isNumber } from '../../../../services/utils';

@Component({
  tag: 'pk-list-stat-item',
  styleUrl: './scss/pk-list-stat-item.scss',
  shadow: true,
})
export class PkListStatItem {
  // Own Properties
  // ====================

  private colorsArray: string[];
  private percentagesArray: number[];
  /**
   * Variable to calculate how much from total represents data.
   * Example: 5/15 - 15 is 100% so 5% is 33% and this is the value we must send to progress indicator
   */
  private percentageForTotalPogress: number;

  // Reference to host HTML element
  // ===================================

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** Specify stats type name **/
  @Prop({ reflect: true }) label: string;

  /** Value of first data to be displayed **/
  @Prop({ reflect: true }) data: number;

  /** Value of second data to be displayed **/
  @Prop({ reflect: true }) secondData: number;

  /** Specify the total value to be used for scale **/
  @Prop() total: number;

  /** Specify data string suffix (if 'total' is set defaults to '/{total}') **/
  @Prop({ reflect: true }) suffix?: string;

  /** Specify data unit of measure **/
  @Prop({ reflect: true }) unitMeasure?: string;

  /** Specify the type of the stat (single, progress, comparison, circular-comparison) **/
  @Prop({ reflect: true }) type: 'single' | 'progress' | 'comparison' | 'circular-comparison' = 'single';

  /** Colors of the progress area element (linear/comparison progress).
   * If only one bar is displayed the default color is data-02.*/
  @Prop({ reflect: true, mutable: true }) progressColor: string;

  /** Color of the secondary progress area element if type is progress */
  @Prop({ reflect: true }) secondaryProgressColor: string = 'data-02';

  /** Background color of the tracking area element if type is progress */
  @Prop({ reflect: true }) trackAreaColor: string = 'ui-01';

  /** Color of the progress bar if value is 0 for data and secondData (comparison type) */
  @Prop({ reflect: true }) progressColorEmpty: string = 'ui-02';

  // Prop lifecycle events
  // =======================
  @Watch('data')
  @Watch('total')
  parseDataProp() {
    if (!this.data && this.data !== 0) return;
    this.setDataAndColorsArray();

    if (!this.total) return;
    this.setTotalPercentage();
  }

  @Watch('progressColor')
  parseProgressColorProp() {
    this.setDataAndColorsArray();
  }

  @Watch('secondData')
  @Watch('secondaryProgressColor')
  updateData() {
    this.setDataAndColorsArray();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  connectedCallback() {
    this.parseDataProp();
  }

  componentWillLoad() {
    if (!this.progressColor) {
      this.progressColor = this.type.includes('comparison') ? 'data-01' : 'data-02';
    }

    this.setTotalPercentage();
    this.setDataAndColorsArray();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private get suffixLabel() {
    if (!isNumber(this.total) || this.suffix) return this.suffix;

    return `/${this.total}`;
  }

  /** Calculate the percentage with total as 100% (only if total is present) */
  private setTotalPercentage = () => {
    if (!this.total) return;

    this.percentageForTotalPogress = (Number(this.data) * 100) / this.total;
  };

  private setDataAndColorsArray = () => {
    if (this.type !== 'comparison') return;

    this.percentagesArray = this.setProgressData();
    this.colorsArray = this.setProgressColor();
  };

  private setProgressData = () => {
    if (this.data === 0 && this.secondData === 0) {
      return [1, 1];
    }

    return [this.data, this.secondData];
  };

  private setProgressColor = () => {
    if (this.data === 0 && this.secondData === 0) {
      return [this.progressColorEmpty, this.progressColorEmpty];
    }

    return [this.progressColor, this.secondaryProgressColor];
  };

  private renderSingleVariant = (): HTMLDivElement => (
    <div part="single-item-container">
      <div class="data-container" part="single-data-container">
        <div class="label" part="single-label">
          {this.label}
        </div>
        <div class="data" part="single-data">
          {this.data ?? '-'}
          {!!this.suffixLabel && this.suffixLabel}
          {this.unitMeasure && <span part="unit-measure">{this.unitMeasure}</span>}
        </div>
      </div>
      <pk-divider part="divider" />
    </div>
  );

  private renderProgressVariant = (): HTMLDivElement => (
    <div part="progress-item-container">
      <div class="data-container" part="progress-data-container">
        <div class="label" part="progress-label">
          {this.label}
        </div>
        <div class="data" part="progress-data">
          {this.data ?? '-'}
          {!!this.suffixLabel && this.suffixLabel}
          {this.unitMeasure && <span part="unit-measure">{this.unitMeasure}</span>}
        </div>
      </div>
      <pk-progress-indicator
        percentages={this.total ? this.percentageForTotalPogress : this.data}
        stroke={4}
        colors={this.progressColor}
        trackAreaColor={this.trackAreaColor}
        part="progress-indicator"
      ></pk-progress-indicator>
    </div>
  );

  private renderComparisonVariant = (): HTMLDivElement => (
    <div part="comparison-item-container">
      <div class="data-container" part="comparison-data-container">
        <div class="data" part="comparison-data">
          {this.data ?? '-'}
          {!!this.suffixLabel && this.suffixLabel}
          {this.unitMeasure && <span part="unit-measure">{this.unitMeasure}</span>}
        </div>
        <div class="label" part="comparison-label">
          {this.label}
        </div>
        <div class="data" part="comparison-second-data">
          {this.secondData ?? '-'}
          {!!this.suffixLabel && this.suffixLabel}
          {this.unitMeasure && <span part="unit-measure">{this.unitMeasure}</span>}
        </div>
      </div>
      <pk-progress-indicator
        percentages={this.percentagesArray}
        stroke={4}
        colors={this.colorsArray}
        trackAreaColor={this.trackAreaColor}
        part="comparison-progress-indicator"
      ></pk-progress-indicator>
    </div>
  );

  private renderCircularComparisonVariant = (): HTMLDivElement => (
    <div part="comparison-item-container">
      <div class="data-container" part="comparison-data-container">
        <pk-progress-indicator
          class="data"
          part="comparison-data"
          shape="circular"
          size={48}
          percentages={this.data}
          stroke={4}
          trackAreaColor={this.trackAreaColor}
          progressColor={this.progressColor}
        >
          {this.data ?? '-'}
          {!!this.suffixLabel && this.suffixLabel}
          {this.unitMeasure && <span part="unit-measure">{this.unitMeasure}</span>}
        </pk-progress-indicator>
        <div class="label" part="comparison-label">
          {this.label}
        </div>
        <pk-progress-indicator
          class="data"
          part="comparison-second-data"
          shape="circular"
          size={48}
          percentages={this.secondData}
          stroke={4}
          trackAreaColor={this.trackAreaColor}
          progressColor={this.secondaryProgressColor}
        >
          {this.secondData ?? '-'}
          {!!this.suffixLabel && this.suffixLabel}
          {this.unitMeasure && <span part="unit-measure">{this.unitMeasure}</span>}
        </pk-progress-indicator>
      </div>
    </div>
  );

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host
        class={{
          divider: true,
          [this.type]: true,
        }}
      >
        {this.type === 'single' && this.renderSingleVariant()}
        {this.type === 'progress' && this.renderProgressVariant()}
        {this.type === 'comparison' && this.renderComparisonVariant()}
        {this.type === 'circular-comparison' && this.renderCircularComparisonVariant()}
      </Host>
    );
  }
}
